import { useAppStore } from '@store'
import { Textures } from '@types'
import { radians } from '@utils/generalMaths/geometry'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import { isPouff } from '@utils/sceneObjects/items/rinconeroUtils'

export const TEXTURE_FABRIC_MAX_REPEAT = 1.1 as const
export const TEXTURE_MODULAR_MAX_REPEAT = 0.85 as const
export const TEXTURE_DEFAULT_MAX_REPEAT = 2 as const

function normalizeRepeat(value: number, maxRepeat: number, minRepeat = 0): number {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue) || numericValue <= 0) return Math.max(minRepeat, 1)
  if (numericValue < 0.25) return Math.min(1.5, maxRepeat)
  const capped = Math.min(numericValue, maxRepeat)
  return minRepeat > 0 ? Math.max(capped, minRepeat) : capped
}

function isFabricDescriptor(texture: Textures[number]): boolean {
  const descriptor = `${texture?.acabado ?? ''} ${texture?.name ?? ''} ${texture?.url ?? ''} ${
    texture?.zoneDisplayName ?? ''
  } ${texture?.zone ?? ''}`.toLowerCase()

  return (
    descriptor.includes('algod') ||
    descriptor.includes('tela') ||
    descriptor.includes('textil') ||
    descriptor.includes('lino') ||
    descriptor.includes('chenille') ||
    descriptor.includes('terciopelo') ||
    descriptor.includes('poliester') ||
    descriptor.includes('fabric') ||
    descriptor.includes('espuma') ||
    descriptor.includes('puff') ||
    descriptor.includes('pouf') ||
    descriptor.includes('sofa') ||
    descriptor.includes('sofá')
  )
}

function isModularSoftItem(itemId?: string): boolean {
  if (!itemId) return false
  const item = useAppStore.getState().items.get(itemId)
  if (!item) return false
  if (isLinkedItem(item)) {
    return isPouff(item.catalogID)
  }
  return false
}

export type ResolvedTextureTiling = {
  repeatX: number
  repeatY: number
  rotation: number
  offsetU: number
  offsetV: number
}

export function isConservativeFabricTexture(texture: Textures[number], itemId?: string): boolean {
  return isModularSoftItem(itemId) || isFabricDescriptor(texture)
}

export function resolveTextureTiling({
  texture,
  repeatX,
  repeatY,
  textureRotation,
  itemHeight,
  itemId,
}: {
  texture: Textures[number]
  repeatX: number
  repeatY: number
  textureRotation: number
  itemHeight: number
  itemId?: string
}): ResolvedTextureTiling {
  const modularSoft = isModularSoftItem(itemId)
  const conservative = modularSoft || isFabricDescriptor(texture)
  const maxRepeat = modularSoft
    ? TEXTURE_MODULAR_MAX_REPEAT
    : conservative
      ? TEXTURE_FABRIC_MAX_REPEAT
      : TEXTURE_DEFAULT_MAX_REPEAT

  const finalRepeatX = normalizeRepeat(repeatX, maxRepeat)
  const finalRepeatY = normalizeRepeat(repeatY, maxRepeat)

  if (conservative) {
    return {
      repeatX: finalRepeatX,
      repeatY: finalRepeatY,
      rotation: radians(textureRotation),
      offsetU: 0,
      offsetV: 0,
    }
  }

  return {
    repeatX: finalRepeatX,
    repeatY: finalRepeatY,
    rotation:
      Math.random() > 0.5 ? radians(textureRotation) : radians(textureRotation + 180),
    offsetU: itemHeight > 1000 ? Math.random() - 0.5 : Math.random() * 2 - 1,
    offsetV: 0,
  }
}
